import { useState, useEffect } from "react";
import { vocabularize } from "src/Common/vocab";
import { FiChevronDown } from "react-icons/fi";
import OutsideClickHandler from "react-outside-click-handler";
import FormTypeDropdown from "src/Pages/forms/templates/components/FormTypeDropdown";
import BranchService from "src/Services/API/branch.service";
import WOService from "src/Services/API/wo.service";
import { handleError } from "src/Common/function";
import WODropdown from "src/Components/filter/components/WODropdown";

// WODropdown is shared with other filters, so it gains no "All" entry of its
// own; the sentinel is prepended to its options here and mapped back to "".
const WO_ALL_OPTION = "All";
const WO_ALL_OPTION_LIST = (numbers) => [WO_ALL_OPTION, ...numbers];

const STATUSES = [
  { value: 0, label: "Draft" },
  { value: 1, label: "Assigned" },
  { value: 2, label: "Filled" },
];

const FREQUENCIES = [
  { value: 0, label: "One Time" },
  { value: 1, label: "Weekly" },
  { value: 2, label: "Monthly" },
  { value: 3, label: "Quarterly" },
  { value: 4, label: "Semi-Annual" },
  { value: 5, label: "Annual" },
];

// Every filter dropdown carries an "All" entry. Selections apply immediately
// now that APPLY and CLEAR are gone, so the dropdown itself has to be the way
// back to unfiltered.
const FilterDropdown = ({ options, selectedValue, onSelect, placeholder = "All" }) => {
  const [isOpen, setIsOpen] = useState(false);

  const selectedLabel =
    selectedValue !== null && selectedValue !== undefined && selectedValue !== ""
      ? options.find((o) => o.value === Number(selectedValue))?.label || placeholder
      : placeholder;

  return (
    <OutsideClickHandler onOutsideClick={() => setIsOpen(false)}>
      <div className="relative w-full">
        <div
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center h-[30px] justify-between px-4 py-2 border rounded-[6px] text-gray-800 focus:outline-none focus:border-blue-500 cursor-pointer"
        >
          <span className="font-OpenSansRegular text-[12px]">{selectedLabel}</span>
          <FiChevronDown className="ml-2" size={15} />
        </div>
        {isOpen && (
          <ul className="absolute z-10 mt-2 w-full bg-white border rounded-md shadow-lg max-h-[300px] overflow-y-auto">
            <li
              onClick={() => {
                onSelect("");
                setIsOpen(false);
              }}
              className="px-4 py-1 cursor-pointer hover:bg-gray-200 border-b-[1px] border-borderColor"
            >
              <span className="font-OpenSansRegular text-[12px]">All</span>
            </li>
            {options.map((opt) => (
              <li
                key={opt.value}
                onClick={() => {
                  onSelect(opt.value);
                  setIsOpen(false);
                }}
                className="px-4 py-1 cursor-pointer hover:bg-gray-200"
              >
                <span className="font-OpenSansRegular text-[12px]">{opt.label}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </OutsideClickHandler>
  );
};

const FormFilterView = ({ onApply }) => {
  const [filterType, setFilterType] = useState(null);
  const [filterFrequency, setFilterFrequency] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterBranch, setFilterBranch] = useState("");
  const [filterWoNumber, setFilterWoNumber] = useState("");

  const [branches, setBranches] = useState([]);
  const [woNumbers, setWoNumbers] = useState([]);

  useEffect(() => {
    fetchBranches();
    fetchWoNumbers();
  }, []);

  const fetchBranches = () => {
    BranchService.getAll()
      .then((response) => {
        if (response.status === 200) {
          setBranches(
            (response.data || []).map((branch) => ({
              value: branch.id,
              label: branch.name,
            }))
          );
        }
      })
      .catch((error) => handleError(error));
  };

  const fetchWoNumbers = () => {
    WOService.getAllOrderNumberWithKeyword("", 0)
      .then((response) => {
        if (response.status === 200) {
          setWoNumbers((response.data || []).map((item) => String(item)));
        }
      })
      .catch((error) => handleError(error));
  };

  // Filters apply as they are chosen — there is no APPLY button. The next values
  // are passed explicitly rather than read back from state, because a state
  // update is not visible in the same tick.
  const applyWith = (overrides) => {
    const next = {
      formType: filterType,
      frequency: filterFrequency,
      status: filterStatus,
      branchId: filterBranch,
      woNumber: filterWoNumber,
      ...overrides,
    };

    onApply({
      formType: next.formType !== null && next.formType !== "" ? String(next.formType) : "",
      frequency: next.frequency,
      status: next.status,
      branchId: next.branchId,
      woNumber: next.woNumber,
    });
  };

  return (
    <div className="flex items-center gap-6 pb-4 flex-wrap">
      <div className="flex items-center gap-2">
        <span className="text-[12px] font-OpenSansMedium font-[600] text-titleColor whitespace-nowrap">
          by Type:
        </span>
        <div className="w-[200px]">
          <FormTypeDropdown
            includeAllOption
            selectedValue={filterType}
            onSelect={(val) => {
              const next = val === "" ? null : val;
              setFilterType(next);
              applyWith({ formType: next });
            }}
          />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-[12px] font-OpenSansMedium font-[600] text-titleColor whitespace-nowrap">
          by Frequency:
        </span>
        <div className="w-[160px]">
          <FilterDropdown
            options={FREQUENCIES}
            selectedValue={filterFrequency}
            onSelect={(val) => {
              const next = val === "" ? "" : String(val);
              setFilterFrequency(next);
              applyWith({ frequency: next });
            }}
          />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-[12px] font-OpenSansMedium font-[600] text-titleColor whitespace-nowrap">
          by Status:
        </span>
        <div className="w-[130px]">
          <FilterDropdown
            options={STATUSES}
            selectedValue={filterStatus}
            onSelect={(val) => {
              const next = val === "" ? "" : String(val);
              setFilterStatus(next);
              applyWith({ status: next });
            }}
          />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-[12px] font-OpenSansMedium font-[600] text-titleColor whitespace-nowrap">
          by Branch:
        </span>
        <div className="w-[180px]">
          <FilterDropdown
            options={branches}
            selectedValue={filterBranch}
            onSelect={(val) => {
              const next = val === "" ? "" : String(val);
              setFilterBranch(next);
              applyWith({ branchId: next });
            }}
            placeholder="Select"
          />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-[12px] font-OpenSansMedium font-[600] text-titleColor whitespace-nowrap">
          {vocabularize("by WO#")}:
        </span>
        <div className="w-[130px]">
          <WODropdown
            options={WO_ALL_OPTION_LIST(woNumbers)}
            selectedWO={filterWoNumber}
            onSelect={(value) => {
              const next = value === WO_ALL_OPTION ? "" : String(value);
              setFilterWoNumber(next);
              applyWith({ woNumber: next });
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default FormFilterView;
